// ============================================================
// WAITING ROOMS - who is staged, and not yet in the fight.
//
// A room holds PARKED COMBATANTS, not characters. A `Combatant` is
// already `{ sheet, overrides, state }`, and `emptyCombatState` gives a
// fresh one - so somebody who has never been deployed and somebody
// recalled at four wounds and on fire are the same shape, and recall is
// lossless without a second code path (v8.17.0).
//
// WHY NOT THE ROSTER. The roster is shared: anything in it is visible
// to every client, and filtering it in the UI would leave an ambush
// sitting in a document any player can read. A staged combatant is
// therefore not on the roster at all - it becomes one at the moment it
// is deployed, which is the only version of "hidden" that is true.
//
// Pure. Zero React dependency. The storage that keeps these secret is
// `multiplayer.ts` and `firestore.rules`; this is only their shape.
// ============================================================
import type { Combatant } from './types';

export interface WaitingRoom {
  id: string;
  name: string;
  /** Where it sits in the rail. */
  order: number;
  /** Front of the list is the front of the queue. */
  entries: Combatant[];
}

export function makeRoom(id: string, name: string, order = 0): WaitingRoom {
  return { id, name, entries: [], order };
}

/**
 * Put people in, at the back.
 *
 * Anyone already there is left where they are rather than moved to the
 * end: importing the same folder twice should not reorder a queue
 * somebody has arranged.
 */
export function addToRoom(room: WaitingRoom, arrivals: Combatant[]): WaitingRoom {
  const present = new Set(room.entries.map((c) => c.id));
  const fresh = arrivals.filter((c) => !present.has(c.id));
  return fresh.length ? { ...room, entries: [...room.entries, ...fresh] } : room;
}

/** Take people out. Returns the SAME room when none of them were in it. */
export function removeFromRoom(room: WaitingRoom, ids: string[]): WaitingRoom {
  const going = new Set(ids);
  const kept = room.entries.filter((c) => !going.has(c.id));
  return kept.length === room.entries.length ? room : { ...room, entries: kept };
}

/**
 * The next `n` out of the front, and what is left behind.
 *
 * FROM THE FRONT, because a room is a queue: the order you put them in
 * is the order they arrive, and a formation drag that drops four should
 * drop the four at the top of the list you are looking at.
 *
 * An emptied room is NOT deleted. It is a place you named, and it
 * outlives whoever happened to be standing in it.
 */
export function takeFrom(room: WaitingRoom, n: number): { taken: Combatant[]; rest: WaitingRoom } {
  const count = Math.max(0, Math.min(Math.floor(n) || 0, room.entries.length));
  if (count === 0) return { taken: [], rest: room };
  return { taken: room.entries.slice(0, count), rest: { ...room, entries: room.entries.slice(count) } };
}

/** Take named people out, wherever they are in the queue. */
export function takeNamed(room: WaitingRoom, ids: string[]): { taken: Combatant[]; rest: WaitingRoom } {
  const wanted = new Set(ids);
  const taken = room.entries.filter((c) => wanted.has(c.id));
  return taken.length ? { taken, rest: removeFromRoom(room, ids) } : { taken: [], rest: room };
}

/** Which room a combatant is standing in, if any. */
export function roomOf(rooms: WaitingRoom[], combatantId: string): WaitingRoom | null {
  return rooms.find((r) => r.entries.some((c) => c.id === combatantId)) ?? null;
}

/**
 * Move somebody between rooms.
 *
 * Returns the SAME array when nothing moved, so a caller can use
 * identity to decide whether anything needs writing.
 */
export function moveBetween(rooms: WaitingRoom[], combatantId: string, toRoomId: string): WaitingRoom[] {
  const from = roomOf(rooms, combatantId);
  if (!from || from.id === toRoomId) return rooms;
  const who = from.entries.find((c) => c.id === combatantId);
  const to = rooms.find((r) => r.id === toRoomId);
  if (!who || !to) return rooms;
  return rooms.map((r) => {
    if (r.id === from.id) return removeFromRoom(r, [combatantId]);
    if (r.id === to.id) return addToRoom(r, [who]);
    return r;
  });
}

/** Rail order: as arranged, then by name for anything that ties. */
export function railOrder(rooms: WaitingRoom[]): WaitingRoom[] {
  return [...rooms].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
}
